import { useEffect, useState } from 'react';
import type { SwampEventResult, SwampEventSummary } from '../types';

const base = process.env.PUBLIC_URL || '';

async function fetchJson<T>(path: string): Promise<T> {
   const response = await fetch(`${base}${path}`);
   if (!response.ok) {
      throw new Error(`Failed to load ${path}`);
   }
   return response.json() as Promise<T>;
}

export function useEventSummaries() {
   const [summaries, setSummaries] = useState<SwampEventSummary[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   useEffect(() => {
      let cancelled = false;

      fetchJson<SwampEventSummary[]>('/data/swamptavern/events-index.json')
         .then((data) => {
            if (!cancelled) {
               setSummaries(data);
               setError(null);
            }
         })
         .catch((err: Error) => {
            if (!cancelled) {
               setError(err.message);
            }
         })
         .finally(() => {
            if (!cancelled) {
               setLoading(false);
            }
         });

      return () => {
         cancelled = true;
      };
   }, []);

   return { summaries, loading, error };
}

export function useEventResult(eventId: string | undefined) {
   const [result, setResult] = useState<SwampEventResult | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   useEffect(() => {
      if (!eventId) {
         setLoading(false);
         setError('No event id provided');
         return;
      }

      let cancelled = false;
      setLoading(true);

      fetchJson<SwampEventResult>(`/data/swamptavern/${eventId}.json`)
         .then((data) => {
            if (!cancelled) {
               setResult(data);
               setError(null);
            }
         })
         .catch((err: Error) => {
            if (!cancelled) {
               setResult(null);
               setError(err.message);
            }
         })
         .finally(() => {
            if (!cancelled) {
               setLoading(false);
            }
         });

      return () => {
         cancelled = true;
      };
   }, [eventId]);

   return { result, loading, error };
}
